/**
 * Sous-formulaire — admin SaaS (super_admin only).
 *
 * Whitelist alignée sur les champs admin de `UpdateTenantSchema` (shared) :
 *   plan, status, trial_ends_at, max_users, max_projects, max_storage_mb.
 *
 * Cette section est rendue dans une `card` au style "alerte" (border ambre)
 * pour bien marquer qu'on touche à des paramètres critiques. Le rendu de la
 * carte est porté par la page parent ; ce composant ne s'occupe que du form.
 */

import { useTranslation } from "react-i18next";

export type AdminSettingsFormState = {
  plan: "trial" | "starter" | "pro" | "enterprise";
  status: "trial" | "active" | "past_due" | "suspended" | "cancelled";
  /** ISO 8601 datetime, ou "" pour null. */
  trial_ends_at: string;
  max_users: number;
  max_projects: number;
  max_storage_mb: number;
};

type Props = {
  value: AdminSettingsFormState;
  onChange: (next: AdminSettingsFormState) => void;
  disabled?: boolean;
};

export function AdminSettingsForm({ value, onChange, disabled }: Props) {
  const { t } = useTranslation();
  const set = <K extends keyof AdminSettingsFormState>(
    key: K,
    v: AdminSettingsFormState[K],
  ) => onChange({ ...value, [key]: v });

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <label className="label" htmlFor="admin-plan">
          {t("settings.admin.plan")}
        </label>
        <select
          id="admin-plan"
          className="input"
          value={value.plan}
          onChange={(e) => set("plan", e.target.value as AdminSettingsFormState["plan"])}
          disabled={disabled}
        >
          <option value="trial">trial</option>
          <option value="starter">starter</option>
          <option value="pro">pro</option>
          <option value="enterprise">enterprise</option>
        </select>
      </div>

      <div>
        <label className="label" htmlFor="admin-status">
          {t("settings.admin.status")}
        </label>
        <select
          id="admin-status"
          className="input"
          value={value.status}
          onChange={(e) => set("status", e.target.value as AdminSettingsFormState["status"])}
          disabled={disabled}
        >
          <option value="trial">trial</option>
          <option value="active">active</option>
          <option value="past_due">past_due</option>
          <option value="suspended">suspended</option>
          <option value="cancelled">cancelled</option>
        </select>
      </div>

      <div className="md:col-span-2">
        <label className="label" htmlFor="admin-trial-ends">
          {t("settings.admin.trial_ends_at")}
        </label>
        <input
          id="admin-trial-ends"
          type="datetime-local"
          className="input"
          value={value.trial_ends_at ? value.trial_ends_at.slice(0, 16) : ""}
          onChange={(e) => {
            const raw = e.target.value;
            // Re-injecte les secondes + Z (UTC) attendu par z.string().datetime()
            set("trial_ends_at", raw ? new Date(raw).toISOString() : "");
          }}
          disabled={disabled}
        />
      </div>

      <div>
        <label className="label" htmlFor="admin-max-users">
          {t("settings.admin.max_users")}
        </label>
        <input
          id="admin-max-users"
          type="number"
          className="input"
          value={value.max_users}
          onChange={(e) => set("max_users", Number(e.target.value) || 0)}
          disabled={disabled}
          min={1}
          max={10_000}
        />
      </div>

      <div>
        <label className="label" htmlFor="admin-max-projects">
          {t("settings.admin.max_projects")}
        </label>
        <input
          id="admin-max-projects"
          type="number"
          className="input"
          value={value.max_projects}
          onChange={(e) => set("max_projects", Number(e.target.value) || 0)}
          disabled={disabled}
          min={1}
          max={100_000}
        />
      </div>

      <div className="md:col-span-2">
        <label className="label" htmlFor="admin-max-storage">
          {t("settings.admin.max_storage_mb")}
        </label>
        <input
          id="admin-max-storage"
          type="number"
          className="input"
          value={value.max_storage_mb}
          onChange={(e) => set("max_storage_mb", Number(e.target.value) || 0)}
          disabled={disabled}
          min={100}
          max={1_000_000}
        />
      </div>
    </div>
  );
}
